import { Separator } from '@avanta/shared/components/ui/Separator';
import { AppointmentDurationLabels, SPECIALITY_ICONS } from '@avanta/shared/constants/appointments';
import { languages, LOCIZE_NS } from '@avanta/shared/constants/common';
import { useFetchSpecialities } from '@avanta/shared/hooks/useFetchSpecialities';
import { useGetSpecialityOptions } from '@avanta/shared/hooks/useGetSpecialityOptions';
import { DEFAULT_LANGUAGE } from '@avanta/shared/types/patients';
import { cn } from '@avanta/shared/utils/cn';
import { formatTime, formatUnifiedDateWithMonth, timezoneOptions } from '@avanta/shared/utils/date';
import { Building, Calendar, Clock, Globe, MapPin, Mic, User } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  AppointmentsReservePayload,
  CONTACT_METHOD_ICONS,
  type AppointmentsReserveFormData,
} from 'pages/Appointments/AppointmentsReserve/types';
import { useUserStore } from 'stores/user';
import { AppointmentType } from 'types/appointments';
import { StepSection } from '../StepSection';
import { SessionSummaryItem } from './SessionSummaryItem';

const PSYCHOLOGISTS_KEY_PREFIX = 'AppointmentsReserve.Steps.psychologist.';

interface SessionSummaryProps {
  title?: string;
  className?: string;
  showLanguages?: boolean;
}

export const SessionSummary = ({
  title,
  className,
  showLanguages = false,
  children,
}: React.PropsWithChildren<SessionSummaryProps>): React.ReactElement => {
  const { watch } = useFormContext<AppointmentsReserveFormData>();
  const patientId = useUserStore(state => state.user?.id);
  const { data: specialities } = useFetchSpecialities(patientId ?? '');
  const centersOptions = useGetSpecialityOptions(AppointmentsReservePayload.backofficeCenterId);

  const {
    specialityBooked,
    contactMethod,
    city,
    backofficeCenterId,
    dateTime,
    timezone,
    language,
    durationType,
  } = watch();
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);

  const speciality = specialities?.find(speciality => speciality.id === specialityBooked);
  const formatDate = formatUnifiedDateWithMonth(dateTime);
  const time = formatTime(dateTime);
  const durationLabel =
    AppointmentDurationLabels[durationType as keyof typeof AppointmentDurationLabels];
  const isRemoteAppointment = contactMethod === AppointmentType.Video;
  const center = centersOptions?.find(
    center => String(center.value) === String(backofficeCenterId)
  );
  const languageKey = language === '' ? DEFAULT_LANGUAGE : language;
  const languageLabel = `${t(languages?.[languageKey]?.label)} (${languages?.[languageKey]?.shortLabel})`;

  return (
    <StepSection title={title} containerClassName="am-lg:gap-2">
      <div
        className={cn(
          'flex flex-col gap-2 am-lg:p-6 p-3 border border-teal-300 rounded-lg am-lg:w-68 w-full',
          className
        )}
      >
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-2 am-lg:grid-cols-1 am-lg:gap-1">
            <SessionSummaryItem
              icon={Calendar}
              label={formatDate}
              className="text-sm am-lg:text-base font-medium"
            />
            <SessionSummaryItem icon={Clock} label={time} className="text-sm am-lg:text-base" />
          </div>
          <Separator className="bg-teal-300 am-lg:my-2 my-1" />
          <div className="grid grid-cols-2 gap-x-7 gap-y-2 am-lg:grid-cols-1 items-start">
            <div className="flex flex-col gap-2 order-3 am-lg:order-0">
              <SessionSummaryItem
                icon={SPECIALITY_ICONS[specialityBooked as keyof typeof SPECIALITY_ICONS] ?? User}
                label={t(
                  `AppointmentsReserve.Steps.speciality.options.${speciality?.name ?? specialityBooked}`
                )}
              />
              <SessionSummaryItem
                icon={
                  CONTACT_METHOD_ICONS[contactMethod as keyof typeof CONTACT_METHOD_ICONS] ?? Mic
                }
                label={`${t(
                  `${PSYCHOLOGISTS_KEY_PREFIX}yourSelection.contactMethod.${contactMethod}`
                )} (${durationLabel})`}
                containerClassName={cn(isRemoteAppointment && 'items-start')}
              />
              {showLanguages && (
                <SessionSummaryItem
                  img={languages[languageKey].image}
                  label={languageLabel}
                  containerClassName={cn('hidden', !isRemoteAppointment && 'flex am-lg:hidden')}
                />
              )}
            </div>
            <div className="flex flex-col gap-2 order-3 am-lg:order-0">
              {isRemoteAppointment && (
                <SessionSummaryItem
                  icon={Globe}
                  label={timezoneOptions.find(option => option.value === timezone)?.label ?? ''}
                />
              )}
              {city && <SessionSummaryItem icon={MapPin} label={city} />}
              {center && (
                <SessionSummaryItem
                  icon={Building}
                  label={center.label}
                  description={center.description}
                  containerClassName="items-start"
                />
              )}
              {showLanguages && (
                <SessionSummaryItem
                  img={languages[languageKey].image}
                  label={languageLabel}
                  containerClassName={cn('flex', !isRemoteAppointment && 'hidden am-lg:flex')}
                />
              )}
            </div>
          </div>
        </div>
        {children}
      </div>
    </StepSection>
  );
};
